import { useState, useEffect, useRef, useCallback } from 'react'
import { extractClaimReference } from '../utils/claimReferenceParser'
import { tryGetClaim, recentClaims } from '../services/claimsApi'
import { useSsoToken } from './useSsoToken'
import { getApiErrorMessage } from '../utils/errorMessages'

/* global Office */

export type DetectionStatus = 'idle' | 'detecting' | 'found' | 'partial-match' | 'not-found' | 'no-reference' | 'error'

export interface DetectionResult {
  status: DetectionStatus
  claimNumber: string | null
  highlightClaimNumber: string | null
  detectedReference: string | null
  errorMessage?: string
}

const INITIAL_STATE: DetectionResult = {
  status: 'idle',
  claimNumber: null,
  highlightClaimNumber: null,
  detectedReference: null,
}

async function readEmailSubject(): Promise<string> {
  const item = Office.context.mailbox.item!
  // Read mode: subject is a string. Compose mode: subject is an object with getAsync.
  if (typeof item.subject === 'string') return item.subject
  return new Promise((resolve, reject) => {
    ;(item.subject as unknown as { getAsync: (cb: (r: Office.AsyncResult<string>) => void) => void }).getAsync(
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value)
        } else {
          reject(new Error(result.error?.message || 'Failed to read email subject'))
        }
      }
    )
  })
}

export function useEmailClaimDetection() {
  const [result, setResult] = useState<DetectionResult>(INITIAL_STATE)
  const { getToken } = useSsoToken()
  const hasRun = useRef(false)

  const detect = useCallback(async () => {
    // In dev mode or when no mailbox item is available, skip detection
    if (!Office?.context?.mailbox?.item) {
      setResult({ ...INITIAL_STATE, status: 'no-reference' })
      return
    }

    setResult({ ...INITIAL_STATE, status: 'detecting' })

    try {
      const subject = await readEmailSubject()

      const reference = extractClaimReference(subject)

      if (!reference) {
        setResult({ ...INITIAL_STATE, status: 'no-reference' })
        return
      }

      // The extracted reference IS the claim number — fetch directly.
      const token = await getToken()
      const claim = await tryGetClaim(reference.value, token)

      if (claim) {
        setResult({
          status: 'found',
          claimNumber: claim.claimNumber,
          highlightClaimNumber: null,
          detectedReference: reference.value,
        })
        return
      }

      // 404 from getClaim — check if reference matches a recent claim number
      const recent = await recentClaims(token)

      const matchingRecent = recent.find((c) => c.claimNumber.toUpperCase() === reference.value.toUpperCase())

      if (matchingRecent) {
        setResult({
          status: 'partial-match',
          claimNumber: null,
          highlightClaimNumber: matchingRecent.claimNumber,
          detectedReference: reference.value,
        })
      } else {
        setResult({
          status: 'not-found',
          claimNumber: null,
          highlightClaimNumber: null,
          detectedReference: reference.value,
        })
      }
    } catch (err) {
      console.error('[EmailClaimDetection] Detection failed:', err)
      setResult({
        ...INITIAL_STATE,
        status: 'error',
        errorMessage: getApiErrorMessage(err, 'Failed to connect and detect claim. Please try again.'),
      })
    }
  }, [getToken])

  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true

    // Call detect in the next tick to avoid "calling setState() directly within an effect" warning
    // as it might lead to unexpected behavior if it triggers immediate re-renders.
    Promise.resolve().then(() => {
      detect()
    })
  }, [detect])

  const reset = () => {
    setResult({ ...INITIAL_STATE, status: 'no-reference' })
  }

  const retry = () => {
    hasRun.current = true // Keep it true so it doesn't double-trigger if useEffect runs again, though it shouldn't
    detect()
  }

  return { ...result, reset, retry }
}
